import { Badge } from '@wedding-tech/ui/badge'
import { Button } from '@wedding-tech/ui/button'
import { AlertCircle, Loader2, UserCheck } from 'lucide-react'

import { FullPageSpinnerPortal } from '@/components/common/Spinner/FullPagePortal'

import { CheckinLayout } from './CheckinLayout'
import type { GuestItem } from './types'

interface ConfirmStepProps {
  selectedGuest: GuestItem
  onBack: () => void
  confirmForm: any
  submitError: string
  isCheckinPending: boolean
}

export function ConfirmStep({
  selectedGuest,
  onBack,
  confirmForm,
  submitError,
  isCheckinPending,
}: ConfirmStepProps) {
  const checkedInCount = selectedGuest.checkedInCount || 0
  const capacity = selectedGuest.expectedFollowers || 1
  const isFullyCheckedIn = checkedInCount >= capacity
  const isPartiallyCheckedIn = checkedInCount > 0 && !isFullyCheckedIn

  return (
    <CheckinLayout
      title="ยืนยันการเช็กอิน"
      subtitle="ตรวจสอบข้อมูลและระบุชื่อที่ต้องการให้แสดงในงาน"
      icon={<UserCheck className="size-7" />}
      onBack={onBack}
    >
      {/* Guest Summary Card */}
      <div className="bg-card space-y-3 rounded-2xl border p-5 shadow-xs">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-muted-foreground text-xs font-medium">
              ข้อมูลผู้มีเกียรติ
            </span>
            <p className="text-foreground text-xl font-bold">
              {selectedGuest.fullName}
            </p>
            {selectedGuest.displayName &&
              selectedGuest.displayName !== selectedGuest.fullName && (
                <p className="text-muted-foreground mt-0.5 text-xs">
                  ชื่อเล่น: {selectedGuest.displayName}
                </p>
              )}
          </div>
          {selectedGuest.tableAssignment?.tableNumber && (
            <div className="bg-primary/10 text-primary rounded-xl px-3 py-1.5 text-center">
              <span className="block text-[10px] font-medium uppercase">
                โต๊ะ
              </span>
              <span className="text-base font-bold">
                {selectedGuest.tableAssignment.tableNumber}
              </span>
            </div>
          )}
        </div>

        <div className="border-border/60 flex flex-wrap items-center gap-1.5 border-t pt-3">
          {selectedGuest.group && (
            <Badge
              variant="outline"
              className="text-xs"
            >
              กลุ่ม: {selectedGuest.group}
            </Badge>
          )}
          <Badge
            variant="outline"
            className="text-muted-foreground text-xs"
          >
            จำนวนที่เชิญ: {capacity} ท่าน
          </Badge>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          confirmForm.handleSubmit()
        }}
        className="space-y-5"
      >
        <confirmForm.AppField name="displayName">
          {(field: any) => (
            <field.TextField
              label="ชื่อที่ต้องการให้แสดงในงาน *"
              placeholder="เช่น พี่สมชาย, น้องพลอย"
            />
          )}
        </confirmForm.AppField>

        {isPartiallyCheckedIn && (
          <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-medium text-amber-800">
            <AlertCircle className="size-4 shrink-0 text-amber-600" />
            <span>
              มีผู้เช็กอินในชื่อนี้แล้ว {checkedInCount}/{capacity} ท่าน
            </span>
          </div>
        )}

        {isFullyCheckedIn && (
          <div className="border-destructive/30 bg-destructive/10 text-destructive flex items-center gap-2 rounded-xl border p-3 text-xs font-medium">
            <AlertCircle className="size-4 shrink-0" />
            <span>
              รายชื่อนี้ทำการเช็กอินครบแล้ว ({checkedInCount}/{capacity} ท่าน)
            </span>
          </div>
        )}

        {submitError && (
          <div className="text-destructive bg-destructive/10 rounded-xl p-3 text-center text-sm font-medium">
            {submitError}
          </div>
        )}

        <div className="pt-2">
          <confirmForm.Subscribe
            selector={(state: any) => ({
              canSubmit: state.canSubmit,
              isSubmitting: state.isSubmitting,
            })}
          >
            {({ canSubmit, isSubmitting }: any) => (
              <Button
                type="submit"
                size="lg"
                disabled={
                  !canSubmit ||
                  isSubmitting ||
                  isCheckinPending ||
                  isFullyCheckedIn
                }
                className="w-full font-semibold"
              >
                {isSubmitting || isCheckinPending ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    กำลังดำเนินการ...
                  </span>
                ) : (
                  'ยืนยันการเช็กอิน'
                )}
              </Button>
            )}
          </confirmForm.Subscribe>
        </div>
      </form>

      {isCheckinPending && <FullPageSpinnerPortal />}
    </CheckinLayout>
  )
}
